import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { Markdown } from 'tiptap-markdown';
import { PaginationPlus } from 'tiptap-pagination-plus';
import { LettersApi } from '@/api';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  RiArrowGoBackLine as UndoIcon,
  RiArrowGoForwardLine as RedoIcon,
  RiH1 as H1Icon,
  RiH2 as H2Icon,
  RiH3 as H3Icon,
  RiBold as BoldIcon,
  RiItalic as ItalicIcon,
  RiStrikethrough as StrikeIcon,
  RiMarkPenLine as HighlightIcon,
  RiAlignLeft as AlignLeftIcon,
  RiAlignCenter as AlignCenterIcon,
  RiAlignRight as AlignRightIcon,
  RiAlignJustify as AlignJustifyIcon,
  RiListOrdered as OrderedListIcon,
  RiListUnordered as BulletListIcon,
  RiCheckFill as CheckIcon,
  RiFileCopyLine as CopyIcon,
  RiSearchEyeLine as CheckSourcesIcon,
  RiMagicLine as RegenerateIcon,
} from 'react-icons/ri';
import clsx from 'clsx';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import './letterEditor.css';

export interface LetterEditorProps {
  initialContent: string;
  onSelectionTextChange?: (text: string) => void;
  onCheckSources?: (text: string) => void;
  letterId?: string;
  caseId?: string;
}

const LetterEditor: React.FC<LetterEditorProps> = ({
  initialContent,
  onSelectionTextChange,
  onCheckSources,
  letterId,
  caseId,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left',
      }),
      Highlight,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      PaginationPlus.configure({
        pageHeight: 940,
        pageGap: 48,
        pageBreakBackground: '#f6f9fc',
        footerRight: '',
        footerLeft: 'Page {page}',
        headerLeft: '',
        headerRight: '',
      }),
      Markdown,
    ],
    content: initialContent || '',
    onSelectionUpdate: ({ editor }) => {
      const state = editor.state;
      const { from, to } = state.selection;
      const selected = editor.state.doc.textBetween(from, to, ' ');
      onSelectionTextChange?.(selected);
    },
    editorProps: {
      attributes: {
        class: 'editor-content w-full focus:outline-none outline-none',
      },
    },
  });

  const isActive = useCallback(
    (name: string, attrs: Record<string, any> = {}) =>
      !!editor?.isActive(name as any, attrs),
    [editor]
  );

  const run = useCallback(
    (fn: (e: typeof editor) => void) => () => {
      if (!editor) return;
      fn(editor);
    },
    [editor]
  );

  const [copied, setCopied] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [showFullRegen, setShowFullRegen] = useState(false);
  const [fullRegenPrompt, setFullRegenPrompt] = useState('');
  const [fullRegenLoading, setFullRegenLoading] = useState(false);
  const [fullRegenError, setFullRegenError] = useState<string | null>(null);
  // Removed sources toggle per request – keeping minimal state
  const [claims] = useState<any[] | null>(null);

  // Auto-save state
  const [lastSavedContent, setLastSavedContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // Auto-save function
  const saveContent = useCallback(
    async (content: string) => {
      if (!letterId || !content) return;

      try {
        setIsSaving(true);
        setSaveError(null);

        await LettersApi.updateLetter(letterId, { content });

        setLastSavedContent(content);
        setLastSaveTime(new Date());
      } catch (error: any) {
        console.error('Failed to save letter:', error);
        setSaveError(error.message || 'Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    },
    [letterId]
  );

  // Auto-save effect - debounced
  useEffect(() => {
    if (!editor) return;

    let saveTimer: NodeJS.Timeout | null = null;

    const updateHandler = () => {
      // Clear existing timer
      if (saveTimer) clearTimeout(saveTimer);

      // Set new timer
      saveTimer = setTimeout(() => {
        const currentContent = editor.getText();

        // Only save if content has changed
        if (currentContent && currentContent !== lastSavedContent) {
          saveContent(currentContent);
        }
      }, 3000); // Save after 3 seconds of no changes
    };

    editor.on('update', updateHandler);

    return () => {
      editor.off('update', updateHandler);
      if (saveTimer) clearTimeout(saveTimer);
    };
  }, [editor, lastSavedContent, saveContent]);

  // Source extraction / highlighting removed (Show sources UI deprecated)

  // Build highlighted HTML (simple span injection; TipTap content is plain text for now)
  // Highlighting logic removed with sources panel
  const copyText = useCallback(async () => {
    if (!editor) return;
    try {
      await navigator.clipboard.writeText(editor.getText());
      setCopied(true);
      analytics.track(ANALYTICS_EVENTS.LETTER_COPIED, {
        letter_id: letterId,
        case_id: caseId,
        text_length: editor.getText().length,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  }, [editor]);

  const toolbarButton = useCallback(
    ({
      onClick,
      active,
      label,
      icon,
    }: {
      onClick: () => void;
      active?: boolean;
      label: string;
      icon: React.ReactNode;
    }) => (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          'flex items-center justify-center rounded px-2 py-1 text-neutral-700 hover:bg-neutral-200 transition-colors',
          active && 'bg-neutral-200 text-neutral-800 border border-neutral-300'
        )}
        aria-label={label}
        title={label}
      >
        {icon}
      </button>
    ),
    []
  );

  return (
    <div className="w-full">
      {/* Toolbar */}
      <div className="sticky top-0 z-50 w-full bg-neutral-50 border-b border-t border-neutral-200 shadow-sm py-2 px-2 flex flex-wrap items-center justify-between gap-2">
        <div className="toolbar flex flex-wrap gap-1 items-center">
          {toolbarButton({
            onClick: run(e => e.chain().focus().undo().run()),
            label: 'Undo',
            icon: <UndoIcon />,
          })}
          {toolbarButton({
            onClick: run(e => e.chain().focus().redo().run()),
            label: 'Redo',
            icon: <RedoIcon />,
          })}

          <div className="block w-px h-5 bg-neutral-300 mx-2" />

          {toolbarButton({
            onClick: run(e =>
              e.chain().focus().toggleHeading({ level: 1 }).run()
            ),
            label: 'H1',
            icon: <H1Icon />,
            active: isActive('heading', { level: 1 }),
          })}
          {toolbarButton({
            onClick: run(e =>
              e.chain().focus().toggleHeading({ level: 2 }).run()
            ),
            label: 'H2',
            icon: <H2Icon />,
            active: isActive('heading', { level: 2 }),
          })}
          {toolbarButton({
            onClick: run(e =>
              e.chain().focus().toggleHeading({ level: 3 }).run()
            ),
            label: 'H3',
            icon: <H3Icon />,
            active: isActive('heading', { level: 3 }),
          })}
          {toolbarButton({
            onClick: run(e => e.chain().focus().setTextAlign('center').run()),
            label: 'Align center',
            icon: <AlignCenterIcon />,
            active: isActive({ textAlign: 'center' } as any),
          })}
          {toolbarButton({
            onClick: run(e => e.chain().focus().setTextAlign('right').run()),
            label: 'Align right',
            icon: <AlignRightIcon />,
            active: isActive({ textAlign: 'right' } as any),
          })}
          {toolbarButton({
            onClick: run(e => e.chain().focus().setTextAlign('justify').run()),
            label: 'Align justify',
            icon: <AlignJustifyIcon />,
            active: isActive({ textAlign: 'justify' } as any),
          })}

          <div className="block w-px h-5 bg-neutral-300 mx-2" />

          {toolbarButton({
            onClick: run(e => e.chain().focus().toggleBulletList().run()),
            label: 'Bullet list',
            icon: <BulletListIcon />,
            active: isActive('bulletList'),
          })}
          {toolbarButton({
            onClick: run(e => e.chain().focus().toggleOrderedList().run()),
            label: 'Ordered list',
            icon: <OrderedListIcon />,
            active: isActive('orderedList'),
          })}

          <div className="block w-px h-5 bg-neutral-300 mx-2" />

          {/* Regenerate Full Letter Button */}
          <button
            type="button"
            onClick={() => setShowFullRegen(true)}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 active:bg-blue-200 transition-colors shadow-sm"
            aria-label="Regenerate Full Letter"
            title="Regenerate Full Letter"
          >
            <RegenerateIcon />
            <span className="text-sm font-medium">Regenerate Letter</span>
          </button>
        </div>

        {/* Save Status Indicator */}
        <div className="flex items-center gap-2 text-sm text-neutral-600 px-2">
          {isSaving && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
              Saving...
            </span>
          )}
          {!isSaving && lastSaveTime && (
            <span className="text-neutral-500">
              Saved {new Date(lastSaveTime).toLocaleTimeString()}
            </span>
          )}
          {saveError && (
            <span className="text-red-600 text-xs">{saveError}</span>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="max-w-4xl w-full mx-auto relative pb-6">
        <div className="w-full min-h-screen px-2 flex flex-col items-center">
          <EditorContent editor={editor} className="editor-content" />
          {editor && (
            <BubbleMenu
              editor={editor}
              appendTo={() => document.body}
              options={{ strategy: 'fixed' }}
              className="rounded-md bg-white border shadow-md px-2.5 py-1.5 text-sm flex items-center gap-1.5 z-[9999]"
              style={{ zIndex: 9999 }}
            >
              <button
                type="button"
                onClick={() => {
                  console.log('Check sources clicked', { letterId, caseId });
                  const { from, to } = editor.state.selection;
                  const text = editor.state.doc
                    .textBetween(from, to, ' ')
                    .trim();
                  if (!text) {
                    console.warn('No text selected for source checking');
                    return;
                  }
                  if (!onCheckSources) {
                    console.error('onCheckSources callback not provided');
                    return;
                  }
                  console.log(
                    'Checking sources for text:',
                    text.substring(0, 50) + '...'
                  );

                  analytics.track(
                    ANALYTICS_EVENTS.LETTER_TEXT_CHECK_SOURCES_CLICKED,
                    {
                      letter_id: letterId,
                      case_id: caseId,
                      text_length: text.length,
                    }
                  );

                  onCheckSources(text);
                }}
                className="flex items-center justify-center rounded-md px-3 py-1.5 text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 active:bg-blue-200 transition-colors shadow-sm"
                aria-label="Check sources"
                title="Check sources"
              >
                <CheckSourcesIcon />
                <span className="ml-1">Check sources</span>
              </button>
              <div className="w-px h-5 bg-neutral-300 mx-1.5" />
              <button
                type="button"
                onClick={() => {
                  const { from, to } = editor.state.selection;
                  const text = editor.state.doc
                    .textBetween(from, to, ' ')
                    .trim();
                  if (!text) return;
                  setRegenError(null);
                  setRegenPrompt('');
                  setShowRegen(true);
                }}
                className="flex items-center justify-center rounded-md px-3 py-1.5 text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 active:bg-amber-200 transition-colors shadow-sm"
                aria-label="Regenerate"
                title="Regenerate"
              >
                <RegenerateIcon />
                <span className="ml-1">Regenerate</span>
              </button>
            </BubbleMenu>
          )}

          {/* Regenerate Modal */}
          {showRegen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/20"
                onClick={() => (!regenLoading ? setShowRegen(false) : null)}
              />
              <div className="relative z-[61] w-full max-w-lg mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-4">
                <h3 className="text-base font-semibold text-neutral-900 mb-2">
                  Regenerate selected text
                </h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Provide an instruction for how to regenerate the selected
                  text.
                </p>
                <textarea
                  value={regenPrompt}
                  onChange={e => setRegenPrompt(e.target.value)}
                  className="w-full border border-neutral-300 rounded-md p-2 text-sm min-h-[96px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. Make it shorter, more formal, fix grammar, add more details..."
                  disabled={regenLoading}
                />
                {regenError && (
                  <div className="mt-2 text-sm text-red-600">{regenError}</div>
                )}
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRegen(false)}
                    className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100"
                    disabled={regenLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!editor) {
                        setRegenError('Editor not initialized');
                        return;
                      }
                      setRegenError(null);
                      const { from, to } = editor.state.selection;
                      const selected = editor.state.doc
                        .textBetween(from, to, ' ')
                        .trim();
                      if (!selected) {
                        setRegenError(
                          'Please select some text in the document.'
                        );
                        return;
                      }
                      if (!letterId) {
                        console.error(
                          'Letter ID is missing. letterId prop:',
                          letterId,
                          'caseId:',
                          caseId
                        );
                        setRegenError(
                          'Letter ID is missing. Please reload the page or go back to the letters list.'
                        );
                        return;
                      }
                      if (!caseId) {
                        setRegenError(
                          'Case ID is missing. This letter may not be associated with a case.'
                        );
                        return;
                      }

                      setRegenLoading(true);

                      analytics.track(
                        ANALYTICS_EVENTS.LETTER_TEXT_REGENERATE_CLICKED,
                        {
                          letter_id: letterId,
                          case_id: caseId,
                          text_length: selected.length,
                          prompt_length: regenPrompt.length,
                        }
                      );

                      try {
                        // Provide some context around selection
                        const CONTEXT = 500;
                        const beforeFrom = Math.max(0, from - CONTEXT);
                        const afterTo = Math.min(
                          editor.state.doc.content.size,
                          to + CONTEXT
                        );
                        const beforeText = editor.state.doc
                          .textBetween(beforeFrom, from, ' ')
                          .trim();
                        const afterText = editor.state.doc
                          .textBetween(to, afterTo, ' ')
                          .trim();

                        const res = await LettersApi.regenerateLetterPartly({
                          letter_id: letterId,
                          user_prompt: regenPrompt || 'Improve this selection',
                          part_of_letter: selected,
                          caseId,
                          context: { beforeText, afterText },
                        });

                        const next = (res as any)?.regenerated_content || '';
                        if (!next) {
                          throw new Error('Empty response from server');
                        }
                        editor.chain().focus().insertContent(next).run();

                        // Save the updated content
                        setTimeout(() => {
                          saveContent(editor.getText());
                        }, 100);

                        setShowRegen(false);
                      } catch (err: any) {
                        console.error('Selection regeneration error:', err);
                        setRegenError(
                          err?.message ||
                            'Failed to regenerate. Please try again.'
                        );
                      } finally {
                        setRegenLoading(false);
                      }
                    }}
                    className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    disabled={regenLoading}
                  >
                    {regenLoading && (
                      <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                    )}
                    Regenerate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Full Letter Regenerate Modal */}
          {showFullRegen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setShowFullRegen(false)}
              />
              <div className="relative z-[61] w-full max-w-lg mx-auto bg-white rounded-xl shadow-xl border border-neutral-200 p-4">
                <h3 className="text-base font-semibold text-neutral-900 mb-2">
                  Regenerate Full Letter
                </h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Provide instructions for how to regenerate the entire letter.
                  The current content will be replaced.
                </p>
                <textarea
                  className="w-full border border-neutral-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={4}
                  placeholder="e.g., Make it more formal, add more technical details, etc."
                  value={fullRegenPrompt}
                  onChange={e => setFullRegenPrompt(e.target.value)}
                  autoFocus
                />
                {fullRegenError && (
                  <p className="text-xs text-red-600 mt-1">{fullRegenError}</p>
                )}
                <div className="flex items-center justify-end gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFullRegen(false);
                      setFullRegenPrompt('');
                      setFullRegenError(null);
                    }}
                    className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 hover:bg-neutral-50"
                    disabled={fullRegenLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!editor) {
                        setFullRegenError('Editor not initialized');
                        return;
                      }
                      if (!letterId) {
                        console.error(
                          'Letter ID is missing for full regen. letterId prop:',
                          letterId,
                          'caseId:',
                          caseId
                        );
                        setFullRegenError(
                          'Letter ID is missing. Please reload the page or go back to the letters list.'
                        );
                        return;
                      }
                      if (!caseId) {
                        setFullRegenError(
                          'Case ID is missing. This letter may not be associated with a case.'
                        );
                        return;
                      }

                      setFullRegenLoading(true);
                      setFullRegenError(null);

                      analytics.track(
                        ANALYTICS_EVENTS.LETTER_REGENERATE_CLICKED,
                        {
                          letter_id: letterId,
                          case_id: caseId,
                          prompt_length: fullRegenPrompt.length,
                        }
                      );

                      try {
                        const fullContent = editor.getText();

                        const res = await LettersApi.regenerateLetterPartly({
                          letter_id: letterId,
                          user_prompt:
                            fullRegenPrompt || 'Regenerate the entire letter',
                          part_of_letter: fullContent,
                          caseId,
                          context: {},
                        });

                        const next = (res as any)?.regenerated_content || '';
                        if (!next) {
                          throw new Error('Empty response from server');
                        }

                        // Replace entire content
                        editor.commands.setContent(next);

                        // Save the updated content
                        setTimeout(() => {
                          saveContent(editor.getText());
                        }, 100);

                        setShowFullRegen(false);
                        setFullRegenPrompt('');
                      } catch (err: any) {
                        console.error('Regeneration error:', err);
                        setFullRegenError(
                          err?.message ||
                            'Failed to regenerate letter. Please try again.'
                        );
                      } finally {
                        setFullRegenLoading(false);
                      }
                    }}
                    className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    disabled={fullRegenLoading}
                  >
                    {fullRegenLoading && (
                      <span className="inline-block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                    )}
                    Regenerate Full Letter
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Copy button */}
          {editor && editor.getText() && (
            <button
              type="button"
              onClick={copyText}
              className="fixed bottom-8 right-8 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-primary-700 transition-colors z-50 inline-flex items-center gap-2"
            >
              {copied ? (
                <>
                  <CheckIcon /> Copied!
                </>
              ) : (
                <>
                  <CopyIcon /> Copy Letter
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LetterEditor;
